/**
 * AzureControlDetailPage - detail d un controle Azure (route /azure/:id).
 *
 * Structure alignee sur ControlDetailPage (M365) :
 *  - Header : id + titre, description, retour
 *  - Bloc statut (Conforme / Non conforme) AVEC le bouton "Exporter la preuve"
 *  - Rationale / impact
 *  - References frameworks (CyFun / CCB toujours en PREMIER)
 *  - Remediation + lien portail Azure
 */
import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Title3, Body1, Caption1, Badge, Card, CardHeader,
  Button, tokens, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { ArrowLeft20Regular, ArrowDownload20Regular, Open16Regular, Eye20Regular } from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { EvidencePreviewDialog, EvidenceValueView } from '../components/EvidencePreviewDialog';
import { getAzureControlById, AZURE_FAMILY_COLORS } from '../domain/azureControls';
import { azureConnectionsApi } from '../api/azureConnectionsApi';
import { azureScansApi, type AzureCheckResult } from '../api/azureScansApi';

const STATUS_CFG: Record<string, { color: string; label: string }> = {
  pass:           { color: '#1F7A45', label: 'Conforme' },
  fail:           { color: '#B82329', label: 'Non conforme' },
  warning:        { color: '#A86317', label: 'À améliorer' },
  manual:         { color: '#4A5E72', label: 'Vérification manuelle' },
  not_applicable: { color: '#7A7A7A', label: 'Non applicable' },
  error:          { color: '#8A2727', label: 'Erreur' },
};

export function AzureControlDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const control = id ? getAzureControlById(id) : undefined;

  // Dernier scan Azure (1ere connexion) pour retrouver le resultat de ce controle.
  const connQ = useQuery({
    queryKey: ['azure-connections'],
    queryFn:  () => azureConnectionsApi.list(),
    staleTime: 60_000, retry: false,
  });
  const connId = connQ.data?.connections?.[0]?.id;
  const scanQ = useQuery({
    queryKey: ['azure-latest', connId],
    queryFn:  () => azureScansApi.latest(connId!),
    enabled:  !!connId, staleTime: 10_000, retry: false,
  });

  const result: AzureCheckResult | null = useMemo(
    () => scanQ.data?.results?.find((x) => x.controlId === id) ?? null,
    [scanQ.data, id],
  );
  const [preview, setPreview] = useState(false);
  const details = (result as unknown as { details?: unknown } | null)?.details;
  const hasDetails = details != null && (typeof details !== 'object' || Object.keys(details as object).length > 0);
  const evidenceCount = hasDetails ? (typeof details === 'object' && !Array.isArray(details) ? Object.keys(details as object).length : 1) : 0;

  if (!control) {
    return (
      <div style={{ padding: 32 }}>
        <MessageBar intent="error"><MessageBarBody>{t('controls.detail.unknownControl', { id })}</MessageBarBody></MessageBar>
        <Link to="/azure" style={{ display: 'inline-block', marginTop: 12 }}>{t('nisAzure.title')}</Link>
      </div>
    );
  }

  const status = result?.status ?? '';
  const sc = STATUS_CFG[status] ?? { color: tokens.colorNeutralForeground3, label: t('controls.list.notScanned') };
  const fam = AZURE_FAMILY_COLORS[control.family];

  // CyFun (CCB) TOUJOURS en premier dans les references.
  const refs = [...control.references].sort(
    (a, b) => (b.framework === 'CyFun' ? 1 : 0) - (a.framework === 'CyFun' ? 1 : 0),
  );

  function exportJson() {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `evidence-${id}-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <PageHeader
        title={`${control.id} — ${control.title}`}
        description={control.description}
        actions={
          <Link to="/azure" style={{ textDecoration: 'none' }}>
            <Button appearance="secondary" icon={<ArrowLeft20Regular />}>{t('nisAzure.title')}</Button>
          </Link>
        }
      />

      {/* Deux blocs de taille égale côte à côte : Résultat | Références framework */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16, alignItems: 'stretch' }}>
        {/* Bloc Résultat */}
        <Card style={{ borderLeft: `4px solid ${sc.color}`, display: 'flex', flexDirection: 'column' }}>
          <CardHeader header={<Title3>{t('controls.detail.resultTitle', 'Résultat')}</Title3>} />
          <div style={{ marginTop: 10, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <Title3 style={{ color: sc.color, margin: 0 }}>{sc.label}</Title3>
              <Badge appearance="filled" style={{ marginLeft: 'auto', background: fam.bg, color: fam.fg, fontWeight: 600 }}>
                {t(`nisAzure.family.${control.family}`)}
              </Badge>
            </div>
            {result?.message
              ? <Body1 style={{ whiteSpace: 'pre-wrap' }}>{result.message}</Body1>
              : <Body1 style={{ color: tokens.colorNeutralForeground3 }}>{t('controls.detail.noResult')}</Body1>}
            <div style={{ marginTop: 'auto', paddingTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Button appearance="primary" size="small" icon={<ArrowDownload20Regular />} onClick={exportJson} disabled={!result}>
                {t('controls.detail.exportEvidence')}
              </Button>
              <Button appearance="secondary" size="small" icon={<Eye20Regular />} onClick={() => setPreview(true)} disabled={!result}>
                {t('controls.detail.previewEvidence', 'Prévisualiser')}
              </Button>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                {t('controls.detail.evidenceCount', { defaultValue: '{{n}} preuve(s) disponible(s)', n: evidenceCount })}
              </Caption1>
            </div>
          </div>
        </Card>

        {/* Bloc Références framework - CyFun (CCB) en premier */}
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader header={<Title3>{t('controls.detail.frameworkRefs')}</Title3>} />
          <div style={{ marginTop: 10 }}>
            {refs.map((r, i) => (
              <div key={i} style={{
                padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <Badge appearance="outline">{r.framework}</Badge>
                <code style={{ fontFamily: 'Consolas, monospace', fontSize: 13 }}>{r.ref}</code>
                <span style={{ flex: 1, color: tokens.colorNeutralForeground2 }}>{r.label}</span>
                {r.cyfunLevel && (
                  <Badge appearance="filled" color={
                    r.cyfunLevel === 'Essential' ? 'danger' : r.cyfunLevel === 'Important' ? 'warning' : 'success'
                  }>
                    {r.cyfunLevel}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Détails de la preuve (pleine largeur) */}
      {hasDetails && (
        <Card style={{ marginBottom: 16 }}>
          <CardHeader header={<Title3>{t('controls.detail.evidencesTitle', { defaultValue: 'Preuve', n: evidenceCount })}</Title3>} />
          <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.6 }}>
            <EvidenceValueView value={details} />
          </div>
        </Card>
      )}

      {/* Rationale / impact (pleine largeur) */}
      {(control.rationale || control.impactIfFails) && (
        <Card style={{ marginBottom: 16 }}>
          {control.rationale && (
            <div>
              <Caption1 style={{ color: tokens.colorNeutralForeground3, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, fontWeight: 700 }}>Rationale</Caption1>
              <Body1 style={{ display: 'block', marginTop: 4 }}>{control.rationale}</Body1>
            </div>
          )}
          {control.impactIfFails && (
            <div style={{ marginTop: 12 }}>
              <Caption1 style={{ color: '#B82329', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, fontWeight: 700 }}>Impact si non conforme</Caption1>
              <Body1 style={{ display: 'block', marginTop: 4 }}>{control.impactIfFails}</Body1>
            </div>
          )}
        </Card>
      )}

      {/* Remediation + lien portail */}
      {(control.remediation || control.azurePortalUrl) && (
        <Card>
          {control.remediation && (
            <>
              <Caption1 style={{ display: 'block', marginBottom: 6, fontWeight: 700, color: tokens.colorBrandForeground1 }}>
                {t('controls.detail.remediationLabel')}
              </Caption1>
              <Body1>{control.remediation}</Body1>
            </>
          )}
          {control.azurePortalUrl && (
            <a href={control.azurePortalUrl} target="_blank" rel="noreferrer"
               style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 12, color: tokens.colorBrandForeground1, textDecoration: 'none' }}>
              <Open16Regular /> {t('nisAzure.openInPortal')}
            </a>
          )}
        </Card>
      )}

      <EvidencePreviewDialog
        open={preview}
        onOpenChange={setPreview}
        title={`${control.id} — ${t('controls.detail.previewEvidence', 'Prévisualiser')}`}
        evidence={result}
        fileName={`evidence-${id}.json`}
      />
    </div>
  );
}
